import {
  ArcRotateCamera,
  Color3,
  Color4,
  Mesh,
  Nullable,
  Scene,
  StandardMaterial,
} from '@babylonjs/core';
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import {
  Fansea3DStageWireframeMode,
  FanseaBackgroundProp,
} from './Fansea3DStage';
import { defaultLightSetups } from './Fansea3DStageDefaultLightSetups';
import Fansea3DStageInspectorState from './Fansea3DStageInspectorState';
import { addLightSetup } from './Fansea3DStageLight';
import { Fansea3DStageTheme } from './Fansea3DStageTheme';
import { FanseaCheckbox } from './FanseaCheckbox';
import { FanseaPrimaryButton } from './FanseaPrimaryButton';
import { FanseaRadioInput } from './FanseaRadioButton';
import { setEnvironmentTexture } from './helpers/sceneHelper';
import { FanseaDropdown } from './FanseaDropdown';

const StyledInspector = styled.div<{ theme: Fansea3DStageTheme }>`
color: ${({ theme }) =>
  theme === Fansea3DStageTheme.Dark ? 'white' : 'white'};

  background: linear-gradient(167.66deg, rgba(15, 15, 15, 0.6) 0%, rgba(26, 26, 26, 0.36) 99.31%);
  backdrop-filter: blur(4px);
  /* Note: backdrop-filter has minimal browser support */
  border-radius: 2px;
  line-height: normal;

  width: 300px;
  height: 100%;
  padding: 25px;
  font-family: 'Fuji', 'Telegraf', sans-serif;
  font-size: 1em;
  box-sizing: border-box;
  position: absolute;
  left: 0;
  top: 0;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    backdrop-filter: blur(2px);
  }

  * {
    box-sizing: border-box;
  }

  &:button {
    margin-right: 64px;
  }

  input:not([type='radio']):not([type='checkbox']),
  select,
  .fansea-3d-stage-toggle {
    width: 100%;
  }

  label {
    display: flex;
    // justify-content: space-between;
  }
  
  .export-button {
    text-decoration: none;
    color: ${({ theme }) =>
      theme === Fansea3DStageTheme.Dark ? 'white' : 'black'};
    background-color: ${({ theme }) =>
      theme === Fansea3DStageTheme.Dark ? 'black' : 'white'};
    border-radius: 4px;
    padding: 8px 24px;
    width: 100%;
    display: block;
    text-align: center;
`;

const Fansea3dStageInspectorTitle = styled.h2`
  font-weight: normal;
  font-size: 1.5em;
  line-height: 2.5rem;
  margin: 75px 0 5px 0;
`;
const Fansea3dStageInspectorDescription = styled.p`
  font-family: 'Arial';
  font-size: 1em;
  line-height: 2rem;
  margin: 1rem 0;
`;

const Fansea3dStageInspectorLabel = styled.label`
  font-family: 'Arial';
  font-size: 1em;
  line-height: 2rem;
  margin: 1rem 0;
`;
const Fansea3dStageInspectorSurfaceWrapper = styled.div`
  margin-top: 25px;
`;
const Fansea3dStageInspectorBackgroundWrapper = styled.div`
  margin-top: 25px;
`;

const Fansea3dStageInspectorSurfaceSelectWrapper = styled.div`
  font-family: 'Arial';
`;

const Fansea3dStageInspectorBackgroundSelectWrapper = styled.div`
  font-family: 'Arial';
`;

const RadioItem = styled(FanseaRadioInput)`
  label {
    padding: 1rem 0;
  }

  svg {
    font-size: 1.5rem;
  }
`;

const CheckboxItem = styled(FanseaCheckbox)`
  label {
    // padding: 1rem 0;
    font-family: 'Arial';
    font-size: 1em;
    line-height: 2rem;
    // margin: 1rem 0;
  }

  svg {
    font-size: 1.5rem;
  }
`;

const FanseaCloseButton = styled(FanseaPrimaryButton)`
  margin-top: 10px;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 100%;
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const FanseaRangeInput = styled.input`
  background-color: white;
  border-radius: 20px;
`;

export interface Fansea3DStageInspectorV2Props {
  customBackgrounds?: FanseaBackgroundProp[];
  background: String;
  setBackground: (background: String) => void;

  wireframeMode: Fansea3DStageWireframeMode;
  setWireframeMode: (wireframeMode: Fansea3DStageWireframeMode) => void;

  lightScale: number;
  showLights: boolean;
  showMirror: boolean;
  mirrorDistance: number;
  mirrorBlur: number;
  mirrorRatio: number;
  theme: Fansea3DStageTheme;
  scene: Nullable<Scene>;
  showExport?: boolean;
  setShowInspector: (showInspector: boolean) => void;
}

const DEFAULT_BACKGROUNDS: FanseaBackgroundProp[] = [
  {
    backgroundTextureUrl:
      'https://res.cloudinary.com/fanseap/raw/upload/v1678108857/2_FANSEA.APP/collectibles/background-textures/dark.env',
    name: 'Dark',
  },
  {
    backgroundTextureUrl:
      'https://res.cloudinary.com/fanseap/raw/upload/v1678108857/2_FANSEA.APP/collectibles/background-textures/light.env',
    name: 'Light',
  },
];

export const Fansea3DStageInspectorV2: FC<Fansea3DStageInspectorV2Props> = ({
  customBackgrounds = [],
  background,
  setBackground,
  wireframeMode,
  setWireframeMode,
  lightScale: initialLightScale,
  showLights: initialShowLights,
  theme: initialTheme,
  setShowInspector,
  scene,
}) => {
  const [theme] = useState<Fansea3DStageTheme>(initialTheme);
  const [wireframeColor] = useState<string>('#00e8df');
  const [showLights, setShowLights] = useState<boolean>(
    Fansea3DStageInspectorState.showLights ?? initialShowLights
  );
  const [lightScale] = useState<number>(
    Fansea3DStageInspectorState.lightScale ?? initialLightScale
  );
  const [lightIntensityScale, setLightIntensityScale] = useState<number>(
    Fansea3DStageInspectorState.lightIntensityScale ?? 5
  );
  const [lightSetup, setLightSetup] = useState<string>(
    Fansea3DStageInspectorState.lightSetup ?? 'Default Environment'
  );
  const [isRotating, setIsRotating] = useState<boolean>(
    Fansea3DStageInspectorState.isAutoRotating ?? true
  );

  const backgrounds = [...DEFAULT_BACKGROUNDS, ...customBackgrounds];

  const onBackgroundChange = async (event: any) => {
    const bgName: string = event.target.value;
    const selectedBackground = backgrounds.find((b) => b.name === bgName);
    console.log(bgName, selectedBackground);
    if (scene && selectedBackground) {
      setBackground(bgName);
      await setEnvironmentTexture(
        selectedBackground.backgroundTextureUrl,
        scene,
        (error) => {
          console.error(error);
        }
      );
    }
  };

  const updateLights = (
    setupName: string,
    showLights: boolean,
    lightScale: number,
    lightIntensityScale: number
  ) => {
    const setup = defaultLightSetups.find((setup) => setup.name === setupName);
    if (setup && scene) {
      addLightSetup(setup, scene, lightIntensityScale, showLights, lightScale);
    }

    Fansea3DStageInspectorState.lightScale = lightScale;
    Fansea3DStageInspectorState.lightIntensityScale = lightIntensityScale;
    Fansea3DStageInspectorState.showLights = showLights;
    Fansea3DStageInspectorState.lightSetup = setupName;

    console.log(Fansea3DStageInspectorState.lightSetup);
  };

  // const onLightScaleChange = (event: any) => {
  //   const value = event.target.value / 100;
  //   setLightScale(value);

  //   updateLights(lightSetup, showLights, lightScale, lightIntensityScale);
  // };

  const onShowLightsChange = (event: any) => {
    const newValue = event.target.checked;
    setShowLights(newValue);

    updateLights(lightSetup, newValue, lightScale, lightIntensityScale);
  };

  const onShowWireframeChange = async (event: any) => {
    const newValue = event.target.value;
    const lastWireframeMode = wireframeMode;
    setWireframeMode(newValue);

    if (scene && newValue !== lastWireframeMode) {
      const model = scene.getNodeByName('model root') as Mesh;
      const modelMeshes = model?.getChildMeshes() || [];

      modelMeshes.forEach((mesh, index) => {
        if (mesh && mesh.material) {
          if (newValue === 'Combined') {
            if (Fansea3DStageInspectorState.defaultMaterial.length) {
              mesh.material =
                Fansea3DStageInspectorState.defaultMaterial[index];
            }
            mesh.enableEdgesRendering(2);
            mesh.edgesWidth = 0.1;
            mesh.edgesColor = Color4.FromHexString(wireframeColor);
          } else if (newValue === 'Only') {
            mesh.disableEdgesRendering();

            // only save default material, if last mode had no wireframes.
            if (lastWireframeMode === Fansea3DStageWireframeMode.None) {
              if (
                typeof Fansea3DStageInspectorState.defaultMaterial ===
                'undefined'
              ) {
                Fansea3DStageInspectorState.defaultMaterial = [];
              }
              Fansea3DStageInspectorState.defaultMaterial[index] =
                mesh.material;
            }

            const wireframeMaterial = new StandardMaterial('wireframe', scene);
            wireframeMaterial.wireframe = true;
            wireframeMaterial.emissiveColor =
              Color3.FromHexString(wireframeColor);
            mesh.material = wireframeMaterial;
          } else {
            if (Fansea3DStageInspectorState.defaultMaterial.length) {
              mesh.material =
                Fansea3DStageInspectorState.defaultMaterial[index];
            }
            mesh.disableEdgesRendering();
          }
        }
      });
    }
  };

  const onIsRotatingChange = (event: any) => {
    console.log('event', event);
    const newValue = event.target.checked;
    setIsRotating(newValue);

    console.log('newValue', newValue);

    Fansea3DStageInspectorState.isAutoRotating = newValue;

    (scene!.activeCamera! as ArcRotateCamera).useAutoRotationBehavior =
      newValue;
  };

  return (
    <StyledInspector theme={theme}>
      <Fansea3dStageInspectorTitle>CUSTOMIZER</Fansea3dStageInspectorTitle>
      <Fansea3dStageInspectorDescription>
        Here you can change the look of your collectible and its surrounding.
      </Fansea3dStageInspectorDescription>

      <Fansea3dStageInspectorSurfaceWrapper>
        <Fansea3dStageInspectorTitle>SURFACE</Fansea3dStageInspectorTitle>
        <Fansea3dStageInspectorSurfaceSelectWrapper>
          <RadioItem
            name={'surface'}
            value={Fansea3DStageWireframeMode.None}
            label={'Original'}
            defaultChecked={wireframeMode === Fansea3DStageWireframeMode.None}
            checked={wireframeMode === Fansea3DStageWireframeMode.None}
            onClick={onShowWireframeChange}
          />
          <RadioItem
            name={'surface'}
            value={Fansea3DStageWireframeMode.Only}
            label={'Wireframe'}
            defaultChecked={wireframeMode === Fansea3DStageWireframeMode.Only}
            checked={wireframeMode === Fansea3DStageWireframeMode.Only}
            onClick={onShowWireframeChange}
          />
        </Fansea3dStageInspectorSurfaceSelectWrapper>
      </Fansea3dStageInspectorSurfaceWrapper>
      <Fansea3dStageInspectorBackgroundWrapper>
        <Fansea3dStageInspectorTitle>BACKGROUND</Fansea3dStageInspectorTitle>
        <Fansea3dStageInspectorBackgroundSelectWrapper>
          {backgrounds?.map((bg, i) => {
            return (
              <RadioItem
                key={i}
                name={'background'}
                value={bg.name}
                label={bg.name}
                checked={background === bg.name}
                defaultChecked={background === bg.name}
                onClick={(e) => {
                  onBackgroundChange(e);
                }}
              />
            );
          })}
        </Fansea3dStageInspectorBackgroundSelectWrapper>
      </Fansea3dStageInspectorBackgroundWrapper>
      <Fansea3dStageInspectorBackgroundWrapper>
        <Fansea3dStageInspectorTitle>ROTATION</Fansea3dStageInspectorTitle>

        <CheckboxItem
          name={'autoRotate'}
          label={'Auto Rotate'}
          checked={isRotating}
          onChange={onIsRotatingChange}
        />
      </Fansea3dStageInspectorBackgroundWrapper>
      <Fansea3dStageInspectorBackgroundWrapper>
        <Fansea3dStageInspectorTitle>LIGHTS</Fansea3dStageInspectorTitle>
        <FanseaDropdown
          name={'lightSetup'}
          value={lightSetup}
          // defaultValue={'Deutschland'}
          label={''}
          placeholder={'Light Setup'}
          items={defaultLightSetups}
          onChange={(e) => {
            const setupName = e.target.value;
            setLightSetup(setupName);
            updateLights(
              setupName,
              showLights,
              lightScale,
              lightIntensityScale
            );
          }}
        />

        {/* <select
          value={lightSetup}
          onChange={(e) => {
            const setupName = e.target.value;
            setLightSetup(setupName);
            updateLights(
              setupName,
              showLights,
              lightScale,
              lightIntensityScale
            );
          }}
        >
          {defaultLightSetups.map((setup) => (
            <option>{setup.name}</option>
          ))}
        </select> */}

        {/* <Fansea3dStageInspectorLabel>Bulb Size</Fansea3dStageInspectorLabel>
        <input
          type="range"
          min="10"
          max="400"
          value={lightScale * 100}
          onChange={onLightScaleChange}
          disabled={lightSetup === 'Default Environment'}
        /> */}
        <Fansea3dStageInspectorLabel>
          Light Intensity
        </Fansea3dStageInspectorLabel>
        <FanseaRangeInput
          type="range"
          min="10"
          max="1000"
          value={lightIntensityScale * 100}
          onChange={(e) => {
            const intensity = parseInt(e.target.value) / 100;
            setLightIntensityScale(intensity);
            updateLights(lightSetup, showLights, lightScale, intensity);
          }}
        />
        <CheckboxItem
          name={'showLights'}
          label={'Show Lights'}
          checked={showLights}
          onChange={onShowLightsChange}
          disabled={lightSetup === 'Default Environment'}
        />
      </Fansea3dStageInspectorBackgroundWrapper>
      <FanseaCloseButton type="button" onClick={() => setShowInspector(false)}>
        Close
      </FanseaCloseButton>
    </StyledInspector>
  );
};
